import { useCallback, useState, useMemo, useEffect, Fragment } from 'react';

import { useNavigate } from 'react-router-dom';

import useAuthStore from '@entities/auth/model/useAuthStore';
import { useDocFormListHtmlQuery } from '@features/form-selection/hooks';
import MainLayout from '@shared/components/organisms/MainLayout/MainLayout';
import TabContentLayout from '@shared/components/organisms/TabContentLayout/TabContentLayout';
import ContentSectionLayout from '@shared/components/organisms/ContentSectionLayout/ContentSectionLayout';
import Alert from '@shared/components/atoms/Alert';
import edit03Icon from '@shared/assets/images/edit-03_icon.svg';

import styles from './FormSelectionPage.module.scss';

/**
 * FormSelectionPage 컴포넌트
 * - 양식 선택 페이지
 * - 문서 양식 목록을 보여주고 선택할 수 있는 화면
 */
export default function FormSelectionPage() {
  const navigate = useNavigate();
  const [filterValues, setFilterValues] = useState({});
  const [selectedForm, setSelectedForm] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  // Alert 상태 관리
  const [alertState, setAlertState] = useState({
    isOpen: false,
    message: '',
  });

  // 회사 코드 가져오기 - Zustand store에서 가져오기
  const companyCode = useAuthStore((state) => state.companyCode);

  // 문서 양식 리스트 API 호출
  const {
    data: docFormList = [],
    isLoading,
    error,
    isFetching,
  } = useDocFormListHtmlQuery(companyCode);

  // API 데이터를 테이블 형식에 맞게 변환
  const formData = useMemo(
    function () {
      return docFormList.map(function (item) {
        return {
          id: item.doc, // 양식 번호
          formName: item.title || '', // 양식 제목
          description: item.form || '', // 양식 설명
          formContent: item.formDataHtml || item.form_data_html || '', // HTML 양식 데이터
          category: item.category || '', // 문서 분류
          requiredDocs: item.requiredDocs || item.required_docs || '', // 필요 서류 가이드
          recipients: item.recipients || [], // 수신/참조자
          executors: item.executors || [], // 시행자
          isFavorite: false, // API 응답에 없음
        };
      });
    },
    [docFormList],
  );
  // TODO: 선택된 ROW 하이라이트 처리 필요

  // 페이지당 항목 수
  const itemsPerPage = 15;

  // 조회된 데이터에서 고유한 category 목록 추출
  const categoryOptions = useMemo(
    function () {
      const categories = formData
        .map(function (item) {
          return item.category;
        })
        .filter(function (category) {
          return category && category.trim() !== '';
        });
      // 중복 제거 및 정렬
      const uniqueCategories = [...new Set(categories)].sort();
      return uniqueCategories.map(function (category) {
        return { label: category, value: category };
      });
    },
    [formData],
  );

  // 필터 필드 정의 (SearchFilter 형식)
  const filterFields = useMemo(
    function () {
      return [
        {
          key: 'documentType',
          type: 'select',
          label: '문서분류',
          value: filterValues.documentType || '',
          options: [{ label: '문서 분류 전체', value: '' }, ...categoryOptions],
        },
        {
          key: 'formName',
          type: 'input',
          label: '양식명',
          placeholder: '',
          value: filterValues.formName || '',
          wide: true,
        },
      ];
    },
    [filterValues.documentType, filterValues.formName, categoryOptions],
  );

  const handleFilterChange = useCallback(function (key, value) {
    setFilterValues(function (prev) {
      return {
        ...prev,
        [key]: value,
      };
    });
  }, []);

  const handleSearch = useCallback(
    function () {
      setCurrentPage(1);
    },
    [filterValues],
  );

  const handleReset = useCallback(function () {
    setFilterValues({
      documentType: '',
      formName: '',
    });
    setCurrentPage(1);
  }, []);

  const handleSelectChange = useCallback(function (formId) {
    setSelectedForm(function (prev) {
      // 이미 선택된 항목을 다시 클릭하면 선택 해제
      if (prev === formId) {
        return null;
      }
      // 새로운 항목 선택 (한 개만 선택 가능)
      return formId;
    });
  }, []);

  const handleWrite = useCallback(
    function () {
      // 작성하기 버튼 클릭 로직 구현
      if (!selectedForm) {
        // Alert 표시
        setAlertState({
          isOpen: true,
          message: '양식을 선택해 주세요',
        });
        return;
      }

      // 선택한 양식 정보 찾기
      const selectedFormData = formData.find(function (form) {
        return form.id === selectedForm;
      });

      if (!selectedFormData) {
        // Alert 표시
        setAlertState({
          isOpen: true,
          message: '선택한 양식 정보를 찾을 수 없습니다.',
        });
        return;
      }

      // 문서 작성 페이지로 이동 (양식 정보를 state로 전달)
      navigate('/approval/new-document/write', {
        state: {
          formId: selectedFormData.id,
          formName: selectedFormData.formName,
          formDescription: selectedFormData.description,
          formContent: selectedFormData.formContent,
          recipients: selectedFormData.recipients,
          executors: selectedFormData.executors,
        },
      });
    },
    [selectedForm, formData, navigate],
  );

  // Alert 닫기 핸들러
  function handleCloseAlert() {
    setAlertState({
      isOpen: false,
      message: '',
    });
  }

  // 필터링된 데이터
  const filteredData = useMemo(
    function () {
      let filtered = formData;

      // 문서분류 필터
      if (filterValues.documentType) {
        filtered = filtered.filter(function (item) {
          return item.category === filterValues.documentType;
        });
      }

      // 양식명 필터
      if (filterValues.formName) {
        filtered = filtered.filter(function (item) {
          return item.formName.toLowerCase().includes(filterValues.formName.toLowerCase());
        });
      }

      return filtered;
    },
    [formData, filterValues],
  );

  // 필터링된 데이터의 페이지네이션
  const filteredTotalPages = useMemo(
    function () {
      return Math.max(1, Math.ceil(filteredData.length / itemsPerPage));
    },
    [filteredData.length, itemsPerPage],
  );

  // 필터링된 데이터의 현재 페이지 데이터
  const paginatedData = useMemo(
    function () {
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      return filteredData.slice(startIndex, endIndex);
    },
    [filteredData, currentPage, itemsPerPage],
  );

  // 페이지 변경 핸들러
  const handlePageChange = useCallback(function (page) {
    setCurrentPage(page);
    // 페이지 변경 시 스크롤을 테이블 상단으로 이동
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // 데이터가 변경되면 첫 페이지로 리셋
  useEffect(
    function () {
      if (currentPage > filteredTotalPages && filteredTotalPages > 0) {
        setCurrentPage(1);
      }
    },
    [filteredTotalPages, currentPage],
  );

  // 행 클릭 핸들러
  const handleRowClick = useCallback(
    function (item) {
      handleSelectChange(item.id);
    },
    [handleSelectChange],
  );

  // 테이블 컬럼 정의
  const tableColumns = [
    {
      key: 'select',
      label: '선택',
      width: '76px',
      className: styles.colSelect,
      render(value, item) {
        return (
          <div className={styles.radioWrapper}>
            <input
              type="radio"
              name="form-selection"
              id={`form-select-${item.id}`}
              checked={selectedForm === item.id}
              onChange={function () {
                handleSelectChange(item.id);
              }}
              onClick={function (e) {
                e.stopPropagation();
              }}
              aria-label="선택"
            />
          </div>
        );
      },
      stopPropagation: true, // 행 클릭 이벤트 전파 방지
    },
    {
      key: 'category',
      label: '문서분류',
      width: '200px',
      className: styles.colCategory,
    },
    {
      key: 'formName',
      label: '양식명',
      width: '341px',
      className: styles.colFormName,
    },
    {
      key: 'description',
      label: '설명',
      width: '400px',
      className: styles.colDescription,
      render(value) {
        return value && String(value).trim() !== '' ? value : '-';
      },
    },
    {
      key: 'requiredDocs',
      label: '필요 서류',
      width: '300px',
      className: styles.colRequiredDocs,
      render(value) {
        if (!value || String(value).trim() === '') return '-';
        const lines = String(value).split('\n');
        return lines.map(function (line, i) {
          return (
            <Fragment key={i}>
              {line}
              {i < lines.length - 1 ? <br /> : null}
            </Fragment>
          );
        });
      },
    },
  ];

  return (
    <MainLayout>
      <TabContentLayout
        tabs={{
          label: '양식 선택',
          content: (
            <>
              {error && <div>데이터를 불러오는 중 오류가 발생했습니다.</div>}
              {!error && (
                <ContentSectionLayout
                  filterFields={filterFields}
                  tableColumns={tableColumns}
                  paginatedData={paginatedData}
                  isEmpty={paginatedData.length === 0}
                  emptyMessage="현재 등록된 양식이 없습니다."
                  currentPage={currentPage}
                  totalPages={filteredTotalPages}
                  onFilterChange={handleFilterChange}
                  onSearch={handleSearch}
                  onReset={handleReset}
                  onPageChange={handlePageChange}
                  onRowClick={handleRowClick}
                  classes={styles}
                  showExcelButton={false}
                  searchButtonText="조회"
                  isLoading={isLoading}
                  isFetching={isFetching}
                  extraContent={
                    <button type="button" className={styles.writeButton} onClick={handleWrite}>
                      <img src={edit03Icon} alt="작성" className={styles.writeButtonIcon} />
                      작성하기
                    </button>
                  }
                />
              )}
            </>
          ),
        }}
      />
      <Alert isOpen={alertState.isOpen} message={alertState.message} onClose={handleCloseAlert} />
    </MainLayout>
  );
}
